/**
 * The shopper's inventory: what they own after a claim, and selling it back. Withdrawing a unit
 * is not here — an order reserves real stock, so it stays the tenant's own server's call.
 */

import { capitalizeTerm, type StorefrontTerminology } from "@loot-os/api-contract";
import type { InventoryItem, SellInventoryResponse } from "@loot-os/client";
import { isLootApiErrorLike } from "@loot-os/react";
import { useMutation } from "@tanstack/react-query";
import { invalidateAfterInventorySale, lootClient } from "./queries.js";

/**
 * A unit can be sold back when the shopper still owns it and it is worth something. The two
 * facts are on the row, so this reads them rather than trusting a flag the server would have to
 * keep in step. A worthless unit is withdraw-only, boxed's rule.
 */
export function isSellable(item: InventoryItem): boolean {
  return item.status === "owned" && item.item.value > 0;
}

/**
 * Per cause: why the unit cannot be shipped, and whether waiting can change that. The sentence
 * the shopper reads is built from these in {@link withdrawalIssueText}.
 */
const WITHDRAWAL_ISSUE_TEXT: Partial<
  Record<
    NonNullable<InventoryItem["withdrawalIssue"]>,
    { reason: string | ((terms: StorefrontTerminology) => string); wait?: string }
  >
> = {
  voucher: {
    reason: (terms) => `${capitalizeTerm(terms.voucher.plural)} have nothing to ship.`,
  },
  listing_unavailable: { reason: "The seller no longer has this, so it cannot be shipped." },
  price_drift: {
    reason: "This costs more than it did, so it cannot be shipped right now.",
    wait: "wait for the price to come down",
  },
  catalog_out_of_stock: {
    reason: "Out of stock, so it cannot be shipped right now.",
    wait: "wait for a restock",
  },
  catalog_unfulfillable: { reason: "This can no longer be shipped." },
};

/**
 * What an owned unit's `withdrawalIssue` means for the shopper, or null when there is nothing to
 * say. A unit that can be sold ends with the sale, because selling is the one action left; a
 * worthless unit cannot be sold (`isSellable`), so its line offers only the wait, if any. The
 * lines differ in whether waiting helps, which is what the shopper decides on.
 */
export function withdrawalIssueText(
  item: InventoryItem,
  terms: StorefrontTerminology,
): string | null {
  if (item.status !== "owned" || !item.withdrawalIssue) return null;
  const text = WITHDRAWAL_ISSUE_TEXT[item.withdrawalIssue];
  if (!text) return null;
  const reason = typeof text.reason === "function" ? text.reason(terms) : text.reason;
  if (isSellable(item)) {
    return `${reason} Sell it for its value${text.wait ? `, or ${text.wait}` : ""}.`;
  }
  if (!text.wait) return reason;
  return `${reason} ${text.wait.charAt(0).toUpperCase()}${text.wait.slice(1)}.`;
}

/** Σ value of the units named, which is what selling them credits. */
export function sellBackCents(items: InventoryItem[]): number {
  return items.reduce((sum, item) => sum + item.item.value, 0);
}

/** Sells the named units for Σ their value; all or nothing, so a refusal sells none of them. */
export function useSellInventoryItems() {
  return useMutation<SellInventoryResponse, unknown, string[]>({
    mutationFn: (inventoryItemIds) => lootClient.sellInventoryItems({ inventoryItemIds }),
    onSuccess: invalidateAfterInventorySale,
  });
}

/**
 * What a refused sale says. Each cause the server names gets its own line, because they ask for
 * different things of the shopper: reload, wait for the shipment, or withdraw instead.
 */
export function saleErrorText(err: unknown, terms: StorefrontTerminology): string {
  if (isLootApiErrorLike(err) && err.code === "inventory_item_not_sellable") {
    const reason = err.details?.reason;
    if (reason === "sold") return "One of these is already sold. Reload to see what you hold.";
    if (reason === "withdrawing") {
      return "One of these is being shipped to you and can no longer be sold.";
    }
    if (reason === "no_value") return "One of these is worth nothing and can only be withdrawn.";
  }
  if (isLootApiErrorLike(err) && err.code === "not_found") {
    return `One of these is no longer in your ${terms.inventory.singular}. Reload to see what you hold.`;
  }
  if (isLootApiErrorLike(err) && err.code === "account_locked") {
    return "This account is locked, so nothing can be sold. Contact support.";
  }
  return err instanceof Error ? err.message : "the sale failed";
}
